// Engine console sidebar menu, as data. Shell.jsx renders it and maps `icon` names to icons.
//
// Entry fields:
//   path      route the entry opens (must be in ROUTE_TABLE, tests/nav-groups.test.mjs checks it)
//   label     text shown
//   icon      icon name, resolved in Shell.jsx
//   roles     optional; only these roles see the entry (omitted means every role)
//   children  optional; entries shown indented under this one (no icon, smaller label)
//   count     optional key into the counts the shell is given; shows a badge when above zero
//   external  optional, replaces `path`: { env, fallback } names the setting that holds the
//             address. The entry opens in a new tab and is hidden when there is no address.
//
// Group fields: label, items, and `collapsible` (the group is drawn closed until opened; it opens
// by itself when the current page is inside it).
//
// A menu entry is added by the change that adds its page, so there is never a dead link.
//
// Left out of the menu on purpose, routes and page files kept (DOC-1414 Q71, Q67, Q15): Sales Pipeline
// (/prospects), Contracts (/contracts), Support (/support), Docs (/docs).
//
// Phase 4 consolidation (2026-03-17):
//   Removed from nav (routes still work): Outbound Pipeline, Subject Lines,
//   Deliverability, Compare, Live Activity, Errors, Queues, Quarantine,
//   Templates, Email Preview, Feature Flags, Notifications.
//   Errors/Queues/Cache/Quarantine → use Observability.
//   FeatureFlags/Notifications → use Settings.
//   Templates/SubjectLines → Phase 8 (Industry merge).
import { isHidden } from './legacy-pages.js'

export const NAV_GROUPS = [
  {
    label: 'OVERVIEW',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
    ]
  },
  {
    label: 'APPROVALS',
    items: [
      { path: '/approvals/pulls', label: 'Lead pulls', icon: 'approvals' },
      { path: '/approvals/drafts', label: 'Drafts', icon: 'drafts' },
      { path: '/approvals/release', label: 'Batch release', icon: 'release' },
    ]
  },
  {
    label: 'REPLIES',
    items: [
      { path: '/replies', label: 'Replies', icon: 'replies' },
      { path: '/replies/unmatched', label: 'Unmatched replies', icon: 'replies', count: 'unmatched' },
    ]
  },
  {
    label: 'CAMPAIGNS',
    items: [
      { path: '/business-campaigns', label: 'Business campaigns', icon: 'campaigns' },
      {
        path: '/outreach-campaigns/new', label: 'Candidate campaigns', icon: 'campaigns',
        children: [
          { path: '/candidate-profiles/new', label: 'Candidate profiles', icon: 'clients' },
          { path: '/apply-jobs', label: 'Queued Apply Jobs', icon: 'drafts' },
        ],
      },
      { path: '/lead-finder', label: 'Lead finder', icon: 'leadDiscovery' },
    ]
  },
  {
    label: 'LEADS',
    items: [
      { path: '/leads', label: 'Leads and segments', icon: 'clients' },
      { path: '/sequences', label: 'Sequences', icon: 'email' },
      { path: '/research', label: 'Research', icon: 'playbooks' },
      { path: '/reports', label: 'Reports', icon: 'reports' },
    ]
  },
  {
    label: 'SENDING',
    items: [
      { path: '/sending-control', label: 'Sending control', icon: 'killSwitch' },
      { path: '/email-accounts', label: 'Email Accounts', icon: 'email' },
      { path: '/do-not-contact', label: 'Do not contact', icon: 'blockList' },
    ]
  },
  {
    label: 'BUSINESS',
    items: [
      { path: '/costs', label: 'Costs', icon: 'costs' },
      { path: '/connections', label: 'Connections', icon: 'activity' },
      { path: '/accounts', label: 'Accounts', icon: 'clients' },
    ]
  },
  {
    label: 'SYSTEM',
    items: [
      { label: 'Growth Portal', icon: 'external', roles: ['admin', 'operator'], external: { env: 'VITE_GROWTH_PORTAL_URL', fallback: 'https://portal.clairvos.com' } },
      { label: 'Machine Room', icon: 'external', roles: ['admin', 'operator'], external: { env: 'VITE_MACHINE_ROOM_URL', fallback: null } },
    ]
  },
  {
    label: 'SETTINGS',
    items: [
      { path: '/audit-log', label: 'Audit log', icon: 'activity', roles: ['admin'] },
      { path: '/people', label: 'People and access', icon: 'clients', roles: ['admin'] },
      { path: '/settings', label: 'Settings', icon: 'settings' },
    ]
  },
  // The old admin pages, still reachable and with their code kept (DOC-1414 Q113). Shown closed.
  // The four whose address an Engine screen now owns live under /legacy/.
  {
    label: 'LEGACY',
    collapsible: true,
    items: [
      { path: '/clients', label: 'Clients', icon: 'clients' },
      { path: '/campaigns', label: 'Legacy campaigns', icon: 'campaigns' },
      { path: '/industries', label: 'Industry', icon: 'playbooks' },
      { path: '/niche-briefs', label: 'Lead Discovery', icon: 'leadDiscovery' },
      { path: '/legacy/replies', label: 'Reply Center', icon: 'replies' },
      { path: '/blocklist', label: 'Block List', icon: 'blockList' },
      { path: '/analytics', label: 'Analytics', icon: 'analytics' },
      { path: '/legacy/costs', label: 'API Costs', icon: 'costs' },
      { path: '/legacy/reports', label: 'Legacy reports', icon: 'reports' },
      { path: '/portal', label: 'Client Portal', icon: 'portal' },
      { path: '/studio', label: 'Clairvos Studio', icon: 'studio' },
      { path: '/observability', label: 'Observability', icon: 'activity' },
      { path: '/quality', label: 'Quality', icon: 'quality' },
      { path: '/kill-switch', label: 'Kill Switch', icon: 'killSwitch', roles: ['admin'] },
      { path: '/billing', label: 'Billing', icon: 'billing', roles: ['admin'] },
      { path: '/legacy/settings', label: 'Legacy settings', icon: 'settings' },
    ]
  },
]

// An address from the build settings, only when it is a plain https address. Anything else is
// treated as not set, so a typo cannot put a script or odd link in the menu.
export function externalAddress(setting, env = {}) {
  if (!setting) return null
  const value = (env[setting.env] || setting.fallback || '').trim()
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null
  } catch {
    return null
  }
}

// Entries one role may see, with children filtered the same way. Each shown entry carries its
// `href` (external ones). A group with nothing left is dropped.
export function getFilteredNavGroups(role, { env = {}, hidden = isHidden, groups = NAV_GROUPS } = {}) {
  const keep = item => (!item.roles || item.roles.includes(role)) && (item.external || !hidden(item.path))
  const resolve = item => {
    if (item.external) {
      const href = externalAddress(item.external, env)
      return href ? { ...item, href } : null
    }
    const children = (item.children || []).filter(keep).map(resolve).filter(Boolean)
    return item.children ? { ...item, children } : item
  }
  return groups.map(group => ({
    ...group,
    items: group.items.filter(keep).map(resolve).filter(Boolean),
  })).filter(group => group.items.length > 0)
}

// Every entry in menu order, children right after their parent.
export function flattenNavItems(groups = NAV_GROUPS) {
  return groups.flatMap(group => group.items.flatMap(item => [item, ...(item.children || [])]))
}

// The entry the current address belongs to: an exact match first, then the longest parent path.
export function activeNavPath(pathname, items = flattenNavItems()) {
  const paths = items.map(item => item.path).filter(Boolean)
  if (paths.includes(pathname)) return pathname
  return paths.filter(path => pathname.startsWith(`${path}/`)).sort((a, b) => b.length - a.length)[0] || null
}

// Badge number for an entry, or null when there is nothing to show.
export function countFor(item, counts = {}) {
  const n = item.count ? counts[item.count] : 0
  return Number.isInteger(n) && n > 0 ? n : null
}

// Badge numbers from the held reply summary. Only a successful read gives a number: not connected and unknown give none, never 0.
export function navCountsFor(held) {
  return held?.kind === 'ready' && Number.isInteger(held.held) ? { unmatched: held.held } : {}
}
